import { isObject, isFunction, isArray } from '@/utils/is';
import { clone } from '@/utils';
import { checkboxFilterNone } from '@/tools/form';

import * as basic_set_form_config from './project/basic-set-form-config';
import * as target_form_config from './project/target-form-config';
import * as optimize_form_config from './project/optimize-form-config';
import * as delivery_range_form_config from './project/delivery-range-form-config';
import * as audience_form_config from './project/audience-form-config';
import * as delivery_setting_form_config from './project/delivery-setting-form-config';
import * as track_url_setting_form_config from './project/track-url-setting-form-config';
import * as channel_form_config from './project/channel-form-config';

const getFilterConfig = () => {
  return {
    /** 基础设置 */
    ...clone(basic_set_form_config),
    /** 投放内容与目标 */
    ...clone(target_form_config),
    /** 优化目标 */
    ...clone(optimize_form_config),
    /** 广告版位 */
    ...clone(delivery_range_form_config),
    /** 定向包 */
    ...clone(audience_form_config),
    /** 排期与预算 */
    ...clone(delivery_setting_form_config),
    /** 监测链接 */
    ...clone(track_url_setting_form_config),
    /** 渠道 */
    ...clone(channel_form_config),
  };
};

/**
 * 项目表单配置
 */
function init(requiredKeyList = [], notRequiredKeyList = []) {
  const filterConfig = getFilterConfig();

  // 必填项的所有key
  const requiredKeys = requiredKeyList.map(item => isObject(item) ? item.key : item);
  // 非必填项的所有key
  const notRequiredKeys = notRequiredKeyList.map(item => isObject(item) ? item.key : item);
  // 必填项的所有复用配置键(对象形式)
  const requiredObjs = requiredKeyList.filter(item => isObject(item));
  // 非必填项的所有复用配置键(对象形式)
  const notRequiredObjs = notRequiredKeyList.filter(item => isObject(item));
  // 所有配置对象的key(包括必填和非必填)
  const filterKeyList = [...requiredKeys, ...notRequiredKeys];
  // 所有复用配置键(对象形式)数组(包括必填和非必填)
  const objKeyList = requiredObjs.concat(notRequiredObjs);

  for (const key in filterConfig) {
    if (filterConfig[key]){
      if (notRequiredKeys.includes(key)) {
        delete filterConfig[key].rules;
      }

      if (!(filterKeyList.includes(key))) {
        delete filterConfig[key];
      }
    }
  }

  addEvent(filterConfig);

  /** 合并复用配置键(对象形式)配置项属性 */
  objKeyList.forEach((obj) => {
    const { key, config } = obj;
    if (key && config) {
      const { on, props, extra, ...args } = config;
      Object.assign(filterConfig[key], { ...args });
      if (on) {
        Object.keys(on).forEach(methodName => {
          filterConfig[key].on = filterConfig[key].on || {};
          addMethod(filterConfig[key].on, methodName, on[methodName]);
        });
      }
      if (filterConfig[key].extra && extra) {
        Object.assign(filterConfig[key].extra, { ...extra });
      }
      if (filterConfig[key].props && props) {
        Object.assign(filterConfig[key].props, { ...props });
      }
    }
  });

  return filterConfig;
}

/**
 * 添加事件
 * @param {*} filterConfig 配置
 */
function addEvent(filterConfig) {
  // 广告版位事件监听
  if (filterConfig?.delivery_range?.children) {
    const {
      inventory_catalog,
      inventory_type,
      union_video_type,
    } = filterConfig.delivery_range.children;

    inventory_catalog.on = {
      change: (val) => {
        const is_manual = inventory_catalog.value === 'MANUAL';
        inventory_type.hidden = !is_manual;

        if (val !== undefined) {
          inventory_type.value = [];
        }
      },
    };

    inventory_type.on = {
      change: (val) => {
        const is_manual = inventory_catalog.value === 'MANUAL';
        union_video_type.hidden = !(is_manual && inventory_type?.value?.toString() === 'INVENTORY_UNION_SLOT');
      },
    };
  }

  // 定向包事件监听
  if (filterConfig?.audience) {
    const {
      district,
      city,
      location_type,
      region_version,
      age,
      ac,

      auto_extend_enabled,
      auto_extend_targets,

      /** 设备 */
      device,
      device_brand,
      phone_prices,
      launch_price,

      /** 行为 */
      interest_action_mode,
      action_scene,
      action_days,
      action_categories,
      action_words,
      interest_categories,
      interest_words,

      /** 人群包 */
      crowd_pack_button,
      crowd_pack,

      hide_if_converted,
      converted_time_duration,
    } = filterConfig.audience.children;

    // 年龄
    if (age) {
      age.on = {
        change: (val) => {
          if (val) {
            age.value = checkboxFilterNone(val);
          }
        },
      };
    }

    // 网络
    if (ac) {
      ac.on = {
        change: (val) => {
          if (val) {
            ac.value = checkboxFilterNone(val);
          }
        },
      };
    }

    // 地域类型事件处理
    if (district) {
      district.on = {
        change: () => {
          city.hidden = true;
          location_type.hidden = district.value === 'NONE';
          city.hidden = district.value === 'NONE';
          region_version.hidden = district.value === 'NONE';
        },
      };
    }

    // 人群包事件处理
    if (crowd_pack_button) {
      crowd_pack_button.on = {
        change: () => {
          if (crowd_pack_button.value === null) {
            crowd_pack.value = {
              include: [],
              exclude: [],
            };
          }

          crowd_pack.hidden = crowd_pack_button.value === null;
        },
      };
    }

    // 过滤已转化用户事件处理
    hide_if_converted.on = {
      change: () => {
        if (
          hide_if_converted.value === 'APP' ||
          hide_if_converted.value === 'CUSTOMER' ||
          hide_if_converted.value === 'ORGANIZATION'
        ) {
          converted_time_duration.hidden = false;
        } else {
          converted_time_duration.hidden = true;
        }
      },
    };

    // 设备事件处理
    if (device) {
      device.on = {
        change: () => {
          device_brand.hidden = device.value === null;
        },
      };
    }

    // 手机价格事件处理
    if (phone_prices) {
      phone_prices.on = {
        change: () => {
          launch_price.hidden = phone_prices.value === null;
          if (phone_prices.value === null) {
            launch_price.value = [];
          } else {
            launch_price.value = [0, 110000];
          }
        },
      };
    }

    // 行为兴趣事件处理
    if (interest_action_mode) {
      interest_action_mode.on = {
        change: () => {
          const is_custom = interest_action_mode.value === 'CUSTOM';
          action_scene.hidden = !is_custom;
          action_days.hidden = !is_custom;
          action_categories.hidden = !is_custom;
          action_words.hidden = !is_custom;
          interest_categories.hidden = !is_custom;
          interest_words.hidden = !is_custom;

          if (is_custom) {
            action_scene.value = ['APP'];
            action_days.value = 30;
          }
        },
      };
    }

    // 智能放量事件处理
    if (auto_extend_enabled) {
      auto_extend_enabled.on = {
        change: () => {
          auto_extend_targets.hidden = !auto_extend_enabled.value;
          auto_extend_targets.value = [];
        },
      };
    }
  }

  // 排期与预算事件监听
  if (filterConfig?.delivery_setting?.children) {
    const {
      schedule_type,
      start_time,
      schedule_time,
      bid_type,
      budget_mode,
      budget,
      budget_optimize_switch,
    } = filterConfig.delivery_setting.children;

    // 投放时间类型事件处理
    schedule_type.on = {
      change: () => {
        const is_custom = schedule_type.value === 'SCHEDULE_START_END';
        start_time.hidden = !is_custom;
        schedule_time.hidden = !is_custom;
      },
    };

    // 竞价策略事件处理
    bid_type.on = {
      change: () => {
        const is_custom = budget_mode.value === 'BUDGET_MODE_DAY';
        const is_no_bid = bid_type.value === 'NO_BID';
        budget_optimize_switch.hidden = !(is_custom && is_no_bid);
      },
    };

    // 预算类型事件处理
    budget_mode.on = {
      change: () => {
        const is_custom = budget_mode.value === 'BUDGET_MODE_DAY';
        const is_no_bid = bid_type.value === 'NO_BID';
        budget_optimize_switch.hidden = !(is_custom && is_no_bid);
        budget.hidden = !is_custom;
      },
    };
  }

  // 监测链接事件监听
  if (filterConfig?.track_url_setting?.children) {
    const {
      track_url_type,
      track_url,
      video_play_effective_track_url,
      video_play_done_track_url,
      video_play_first_track_url,
      action_track_url,
    } = filterConfig.track_url_setting.children;
    track_url_type.on = {
      change: () => {
        const is_custom = track_url_type.value === 'CUSTOM';

        track_url.hidden = !is_custom;
        video_play_effective_track_url.hidden = !is_custom;
        video_play_done_track_url.hidden = !is_custom;
        video_play_first_track_url.hidden = !is_custom;
        action_track_url.hidden = !is_custom;
        if (isArray(action_track_url.value) && action_track_url.value.length) {
          action_track_url.value = action_track_url.value[0];
        }
      },
    };
  }
}

/**
 * 配置方法追加执行
 * @param {Object} target 追加方法的目标对象
 * @param {String} methodName 方法名
 * @param {Function} method 要追加执行的方法
 */
function addMethod(target = {}, methodName, method) {
  if (!isFunction(target[methodName])) {
    target[methodName] = method;
  } else {
    const oldMethods = target[methodName];
    target[methodName] = (...args) => {
      oldMethods(...args);
      method(...args);
    };
  }
}

export default init;
